/* ca-video-autoplay.js — keep muted background video playing on iOS Low Power Mode.
 *
 * THE PROBLEM
 * iOS Low Power Mode blocks <video autoplay> outright, even when the video is
 * muted + playsinline (which normally satisfies Safari's autoplay policy). The
 * element simply never starts, and because we ship no controls on background
 * video the visitor is left staring at a frozen poster frame with no way to
 * start it. Android battery-saver and Safari's "Auto-Play: Never" per-site
 * setting fail the same way.
 *
 * WHAT ACTUALLY WORKS
 * Nothing can force playback without a user gesture — that is the whole point
 * of the policy and no amount of JS gets around it. What we can do is:
 *   1. ask politely on load, which succeeds everywhere except LPM;
 *   2. if the promise rejects, arm a ONE-SHOT listener on the first real user
 *      interaction anywhere on the page and retry then. In LPM a gesture
 *      unlocks playback, so the video starts the moment the visitor taps or
 *      scrolls — which is almost immediately on a page like this;
 *   3. retry when the tab becomes visible again or the page is restored from
 *      the bfcache (Safari pauses and does not resume on its own).
 *
 * The poster attribute is what the visitor sees until then, so every autoplay
 * background video SHOULD carry one. That is a real still frame, not a gap.
 *
 * Applies to any <video autoplay> in the document, so new sections are covered
 * without touching this file. Videos marked data-ca-noautoplay are skipped, and
 * anything with a `controls` attribute is left completely alone — if the
 * visitor has a play button, that is their decision to make, not ours.
 */
(function () {
  'use strict';

  var GESTURES = ['touchstart', 'pointerdown', 'mousedown', 'keydown', 'scroll'];
  var pending = [];          // videos still waiting on a user gesture
  var armed = false;

  function isCandidate(v) {
    return v.hasAttribute('autoplay') &&
           !v.hasAttribute('controls') &&
           !v.hasAttribute('data-ca-noautoplay');
  }

  function attempt(v) {
    // Re-assert the attributes Safari insists on. A theme editor reload or a
    // third-party script can strip `muted`, and an unmuted video is refused
    // even on mains power.
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    if (!v.hasAttribute('playsinline')) v.setAttribute('playsinline', '');

    var p;
    try {
      p = v.play();
    } catch (e) {
      queue(v);
      return;
    }
    // Older browsers return undefined rather than a promise.
    if (p && typeof p.then === 'function') {
      p.then(function () { unqueue(v); })
       .catch(function () { queue(v); });
    }
  }

  function queue(v) {
    if (pending.indexOf(v) === -1) pending.push(v);
    arm();
  }

  function unqueue(v) {
    var i = pending.indexOf(v);
    if (i !== -1) pending.splice(i, 1);
  }

  function retryAll() {
    // Copy first: attempt() mutates `pending` via unqueue on success.
    pending.slice().forEach(attempt);
  }

  function arm() {
    if (armed) return;
    armed = true;
    GESTURES.forEach(function (evt) {
      document.addEventListener(evt, onGesture, { passive: true, capture: true });
    });
  }

  function disarm() {
    GESTURES.forEach(function (evt) {
      document.removeEventListener(evt, onGesture, { capture: true });
    });
    armed = false;
  }

  function onGesture() {
    retryAll();
    // One shot. If a video still refuses after a genuine user gesture the
    // browser is telling us no for a reason we cannot fix, and re-arming on
    // every scroll event would just burn battery on the device that is already
    // in Low Power Mode.
    disarm();
  }

  function scan() {
    var vids = document.querySelectorAll('video[autoplay]');
    for (var i = 0; i < vids.length; i++) {
      if (isCandidate(vids[i])) attempt(vids[i]);
    }
  }

  function init() {
    scan();

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) retryAll();
    });

    // Safari restores from the bfcache with the video paused.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) retryAll();
    });

    // Sections re-render on every change in the theme editor.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', scan);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
